import React from 'react';
import { User, MessageCircle, TrendingUp, Zap, ArrowRight, Shield, AlertTriangle } from 'lucide-react';

// Alipay Icon
const AlipayIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
    <path d="M21.422 15.358c-3.83-1.153-6.055-1.84-7.664-2.37 1.071-1.665 1.88-3.584 2.295-5.403H9.67V6.142h3.72V4.89H9.67V2.25H7.89v2.64H4.06v1.252h3.83v1.443H4.48v1.252h9.198c-.353 1.288-.955 2.53-1.737 3.627-1.914-.555-4.034-.97-6.432-.97C2.312 11.494 0 13.264 0 15.645c0 2.38 2.083 3.936 5.317 3.936 3.126 0 5.726-1.438 7.666-3.627.895.418 1.91.863 3.087 1.38l3.144 1.438c.537.239 1.16.06 1.472-.44l1.608-2.574c.313-.5.165-1.153-.335-1.446l-1.537-.954zm-16.105 2.91c-2.186 0-3.58-1.01-3.58-2.382 0-1.373 1.11-2.382 3.19-2.382 1.88 0 3.724.358 5.388.895-1.468 2.24-3.248 3.87-4.998 3.87z"/>
  </svg>
);

// WeChat Icon
const WeChatIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
    <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088v-.035h-.406zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z"/>
  </svg>
);

const MainMenu = ({ setCurrentScreen, setProfileActiveSection, exchangeRate, usdtToCnyRate, userFirstName, userPhotoUrl, verificationStatus }) => {
  const isAdmin = verificationStatus?.isAdmin || false;
  const isVerified = verificationStatus?.isVerified || isAdmin || false;
  const isVerificationLoading = verificationStatus?.isLoading || false;
  
  return (
  <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black p-4">
    <div className="max-w-md mx-auto pt-28">
      
      {/* Header Section */}
      <div className="text-center mb-6">
        <img 
          src="/logo.png" 
          alt="Kit8 Exchange" 
          className="w-24 h-auto mx-auto mb-3"
          loading="eager"
          fetchpriority="high"
        />
        <h1 className="text-2xl font-bold text-white mb-1">Kit8 Exchange</h1>
        <p className="text-gray-500 text-sm">Пополнение Alipay и WeChat</p>
      </div>

      {/* User Greeting */}
      <div className="bg-gray-800/50 rounded-2xl p-4 mb-4 border border-gray-700/50">
        <div className="flex items-center gap-3">
          <div className="relative">
            {userPhotoUrl ? (
              <img 
                src={userPhotoUrl} 
                alt="Profile" 
                className="w-12 h-12 rounded-full border-2 border-purple-500/30 object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center border-2 border-purple-500/30">
                <User className="w-6 h-6 text-purple-400" />
              </div>
            )}
            {/* Verification/Admin badge */}
            {!isVerificationLoading && (
              <div className={`absolute -bottom-1 -right-1 rounded-full p-0.5 border-2 border-gray-900 ${
                isAdmin ? 'bg-purple-500' : isVerified ? 'bg-green-500' : 'bg-amber-500'
              }`}>
                {isAdmin ? (
                  <span className="w-3 h-3 text-white flex items-center justify-center text-xs">👑</span>
                ) : isVerified ? (
                  <Shield className="w-3 h-3 text-white" />
                ) : (
                  <AlertTriangle className="w-3 h-3 text-white" />
                )}
              </div>
            )}
          </div>
          <div className="flex-1">
            <p className="text-gray-400 text-sm">Добро пожаловать,</p>
            <div className="flex items-center gap-2">
              <p className="text-white font-semibold text-lg">
                {userFirstName || 'Гость'}
              </p>
              {isAdmin && (
                <span className="px-2 py-0.5 rounded-full bg-purple-500/20 border border-purple-500/50 text-purple-400 text-xs font-medium">
                  Admin
                </span>
              )}
            </div>
          </div>
          {/* Verification status - show only for non-admins */}
          {!isVerificationLoading && !isVerified && !isAdmin && (
            <button
              onClick={() => {
                setProfileActiveSection('kyc');
                setCurrentScreen('profile');
              }}
              className="px-3 py-1.5 rounded-lg bg-amber-500/20 border border-amber-500/50 text-amber-400 text-xs font-medium hover:bg-amber-500/30 transition-all"
            >
              KYC
            </button>
          )}
        </div>
      </div>

      {/* Exchange Rates */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="bg-gray-800/50 rounded-2xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-blue-400" />
            <span className="text-xs text-gray-500">RUB → CNY</span>
          </div>
          <p className="text-xl font-bold text-white">
            {exchangeRate ? exchangeRate : '—'}
            <span className="text-sm text-gray-400 ml-1">₽/¥</span>
          </p>
        </div>

        <div className="bg-gray-800/50 rounded-2xl p-4 border border-gray-700/50">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-green-400" />
            <span className="text-xs text-gray-500">USDT → CNY</span>
          </div>
          <p className="text-xl font-bold text-white">
            {usdtToCnyRate ? usdtToCnyRate : '—'}
            <span className="text-sm text-gray-400 ml-1">$/¥</span>
          </p>
        </div>
      </div>

      {/* Main Actions */}
      <div className="space-y-3 mb-6">
        {/* Profile Button */}
        <button
          onClick={() => setCurrentScreen('profile')}
          className="w-full bg-gray-800 rounded-2xl py-4 px-5 flex items-center justify-between border border-gray-700 hover:bg-gray-750 hover:border-purple-500/50 transition-all"
        >
          <div className="flex items-center gap-4">
            <div className="bg-purple-500/20 rounded-xl p-2.5">
              <User className="w-5 h-5 text-purple-400" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-white">Личный кабинет</p>
              <p className="text-gray-500 text-xs">Заявки и история</p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-500" />
        </button>

        {/* Alipay Button */}
        <button
          onClick={() => setCurrentScreen('deposit')}
          className="w-full bg-gray-800 rounded-2xl py-4 px-5 flex items-center justify-between border border-gray-700 hover:bg-gray-750 hover:border-blue-500/50 transition-all"
        >
          <div className="flex items-center gap-4">
            <div className="bg-blue-500 rounded-xl p-2.5">
              <AlipayIcon />
            </div>
            <div className="text-left">
              <p className="font-semibold text-white">Alipay</p>
              <p className="text-gray-500 text-xs">Пополнить кошелёк</p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-500" />
        </button>

        {/* WeChat Button */}
        <button
          onClick={() => setCurrentScreen('wechat')}
          className="w-full bg-gray-800 rounded-2xl py-4 px-5 flex items-center justify-between border border-gray-700 hover:bg-gray-750 hover:border-green-500/50 transition-all"
        >
          <div className="flex items-center gap-4">
            <div className="bg-green-500 rounded-xl p-2.5">
              <WeChatIcon />
            </div>
            <div className="text-left">
              <p className="font-semibold text-white">WeChat Pay</p>
              <p className="text-gray-500 text-xs">Пополнить кошелёк</p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Help Button */}
      <button
        onClick={() => setCurrentScreen('support')}
        className="w-full bg-gray-800/50 rounded-2xl py-3 px-5 flex items-center justify-center gap-2 border border-gray-700/50 hover:bg-gray-800 transition-all"
      >
        <MessageCircle className="w-4 h-4 text-gray-500" />
        <span className="text-gray-400 text-sm">Помощь</span>
      </button>

      {/* Footer */}
      <p className="text-center text-xs text-gray-600 mt-8">Безопасные переводы 24/7</p>
    </div>
  </div>
  );
};

export default MainMenu;
